import { client as axios } from '../../axios/client';
import {
  GET_COLUMNS,
  GET_COLUMNS_SUCCESS,
  GET_COLUMNS_ERROR,
  CREATE_COLUMN,
  CREATE_COLUMN_SUCCESS,
  CREATE_COLUMN_ERROR,
  UPDATE_COLUMN,
  UPDATE_COLUMN_SUCCESS,
  UPDATE_COLUMN_ERROR,
  DELETE_COLUMN,
  DELETE_COLUMN_SUCCESS,
  DELETE_COLUMN_ERROR,
  REORDER_COLUMNS,
  REORDER_COLUMNS_SUCCESS,
  REORDER_COLUMNS_ERROR,
} from './kanbanTypes';

/**
 * Obtiene todas las columnas de un tablero
 */
export const getColumns = (boardId, token, preserveTickets = true) => async (dispatch, getState) => {
  dispatch({ type: GET_COLUMNS });

  try {
    if (!boardId) {
      throw new Error('ID del tablero no proporcionado');
    }

    if (!token) {
      throw new Error('Token de autenticación no proporcionado');
    }

    // Asegurarnos de que el token tenga el formato correcto
    const authToken = token.startsWith('Bearer ') ? token : `Bearer ${token}`;

    let currentColumnsWithTickets = [];
    if (preserveTickets) {
      const state = getState();
      const currentColumns = state.kanban.columns?.items || [];
      currentColumnsWithTickets = currentColumns.filter(col =>
        String(col.board_id) === String(boardId) && col.tickets && col.tickets.length > 0
      );
    }

    // Verificar si el boardId es un número o un token JWT
    let boardIdToUse = boardId;

    // Convertir boardId a string para las verificaciones
    const boardIdStr = String(boardId);

    // Si el boardId parece ser un token JWT (contiene puntos y es largo)
    if (typeof boardId === 'string' && boardIdStr.includes('.') && boardIdStr.length > 50) {
      console.warn('Se detectó un token JWT como boardId. Esto no es correcto. El boardId debe ser un identificador numérico.');

      // Intentar extraer un ID numérico si está al principio del string
      const possibleId = boardIdStr.split(/[^0-9]/)[0];
      if (possibleId && !isNaN(parseInt(possibleId))) {
        boardIdToUse = possibleId;
      } else {
        throw new Error('El ID del tablero proporcionado parece ser un token JWT. Debe proporcionar un ID numérico.');
      }
    } else if (boardIdStr.includes('/')) {
      // Si el boardId contiene una barra, tomar la primera parte
      boardIdToUse = boardIdStr.split('/')[0];
    }

    // Reintento automático para errores transitorios (timeout, red, 5xx)
    const MAX_RETRIES = 1;
    let response;
    let lastError;

    for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
      try {
        response = await axios.get(`/kanban/boards/${boardIdToUse}/columns`, {
          headers: {
            'Authorization': authToken,
            'Content-Type': 'application/json'
          },
          timeout: 10000
        });
        lastError = null;
        break;
      } catch (err) {
        lastError = err;
        const isTransient = !err.response || err.response.status >= 500;
        if (attempt < MAX_RETRIES && isTransient) {
          await new Promise(r => setTimeout(r, 1000));
          continue;
        }
        throw err;
      }
    }

    if (lastError) throw lastError;

    let columns = [];

    if (response && response.data) {
      if (Array.isArray(response.data)) {
        columns = response.data;
      } else if (response.data.items && Array.isArray(response.data.items)) {
        columns = response.data.items;
      } else {
        console.warn('getColumns: Formato de respuesta inesperado', response.data);
      }
    }

    columns = columns.map(column => {
      const existingColumn = currentColumnsWithTickets.find(col => String(col.id) === String(column.id));
      const tickets = existingColumn && existingColumn.tickets ? existingColumn.tickets : [];

      return {
        ...column,
        board_id: boardId,
        tickets: preserveTickets ? tickets : []
      };
    });

    dispatch({
      type: GET_COLUMNS_SUCCESS,
      payload: columns
    });

    return { payload: columns };
  } catch (error) {
    // Manejar errores específicos
    let errorMessage = 'Error al obtener columnas';

    if (error.response) {
      const status = error.response.status;

      if (status === 401) {
        errorMessage = 'Sesión expirada. Por favor inicie sesión nuevamente.';
      } else if (status === 403) {
        errorMessage = 'No tiene permisos para acceder a este tablero.';
      } else if (status === 404) {
        errorMessage = 'El tablero no existe o ha sido eliminado.';
      } else {
        errorMessage = `Error del servidor (${status}): ${error.response.statusText || 'Error desconocido'}`;
      }
    } else if (error.code === 'ECONNABORTED') {
      errorMessage = 'La solicitud tomó demasiado tiempo. Compruebe su conexión.';
    } else if (error.message) {
      errorMessage = error.message;
    }
    // Despachar error
    dispatch({
      type: GET_COLUMNS_ERROR,
      payload: errorMessage
    });

    // Propagar el error para que el caller lo maneje
    throw new Error(errorMessage);
  }
};

/**
 * Obtiene columnas de un tablero con paginación para scroll infinito
 */
export const getColumnsPaginated = (boardId, offset = 0, limit = 5, token, preserveTickets = true) => async (dispatch, getState) => {
  dispatch({ type: GET_COLUMNS });

  try {
    if (!boardId) {
      throw new Error('ID del tablero no proporcionado');
    }

    if (!token) {
      throw new Error('Token de autenticación no proporcionado');
    }

    // Asegurarnos de que el token tenga el formato correcto
    const authToken = token.startsWith('Bearer ') ? token : `Bearer ${token}`;

    let currentColumnsWithTickets = [];
    if (preserveTickets) {
      const state = getState();
      const currentColumns = state.kanban.columns?.items || [];
      currentColumnsWithTickets = currentColumns.filter(col =>
        String(col.board_id) === String(boardId) && col.tickets && col.tickets.length > 0
      );
    }

    // Verificar si el boardId es un número o un token JWT
    let boardIdToUse = boardId;

    // Convertir boardId a string para las verificaciones
    const boardIdStr = String(boardId);

    // Si el boardId parece ser un token JWT (contiene puntos y es largo)
    if (typeof boardId === 'string' && boardIdStr.includes('.') && boardIdStr.length > 50) {
      console.warn('Se detectó un token JWT como boardId. Esto no es correcto. El boardId debe ser un identificador numérico.');

      // Intentar extraer un ID numérico si está al principio del string
      const possibleId = boardIdStr.split(/[^0-9]/)[0];
      if (possibleId && !isNaN(parseInt(possibleId))) {
        boardIdToUse = possibleId;
      } else {
        throw new Error('El ID del tablero proporcionado parece ser un token JWT. Debe proporcionar un ID numérico.');
      }
    } else if (boardIdStr.includes('/')) {
      // Si el boardId contiene una barra, tomar la primera parte
      boardIdToUse = boardIdStr.split('/')[0];
    }

    const response = await axios.get(`/kanban/boards/${boardIdToUse}/columns`, {
      params: {
        offset,
        limit
      },
      headers: {
        'Authorization': authToken,
        'Content-Type': 'application/json'
      },
      timeout: 10000
    });

    let columns = [];

    if (response && response.data) {
      if (Array.isArray(response.data)) {
        columns = response.data;
      } else if (response.data.items && Array.isArray(response.data.items)) {
        columns = response.data.items;
      } else {
        console.warn('getColumnsPaginated: Formato de respuesta inesperado', response.data);
      }
    }

    columns = columns.map(column => {
      const existingColumn = currentColumnsWithTickets.find(col => String(col.id) === String(column.id));
      const tickets = existingColumn && existingColumn.tickets ? existingColumn.tickets : [];

      return {
        ...column,
        board_id: boardId,
        tickets: preserveTickets ? tickets : []
      };
    });

    // Si offset > 0, entonces estamos cargando más columnas (append mode)
    if (offset > 0) {
      const state = getState();
      const existingColumns = state.kanban.columns?.items || [];
      const existingBoardColumns = existingColumns.filter(col => 
        String(col.board_id) === String(boardId)
      );
      
      // Evitar duplicados al agregar nuevas columnas
      const newColumns = columns.filter(newCol => 
        !existingBoardColumns.some(existing => String(existing.id) === String(newCol.id))
      );
      
      const allColumns = [...existingBoardColumns, ...newColumns];
      
      dispatch({
        type: GET_COLUMNS_SUCCESS,
        payload: allColumns,
        meta: { isAppend: true, offset, limit, hasMore: columns.length === limit }
      });

      return { 
        payload: allColumns, 
        newColumns,
        hasMore: columns.length === limit,
        totalLoaded: allColumns.length
      };
    } else {
      // Primera carga (replace mode)
      dispatch({
        type: GET_COLUMNS_SUCCESS,
        payload: columns,
        meta: { isAppend: false, offset, limit, hasMore: columns.length === limit }
      });

      return { 
        payload: columns, 
        hasMore: columns.length === limit,
        totalLoaded: columns.length
      };
    }

  } catch (error) {
    // Manejar errores específicos
    let errorMessage = 'Error al obtener columnas paginadas';

    if (error.response) {
      const status = error.response.status;

      if (status === 401) {
        errorMessage = 'Sesión expirada. Por favor inicie sesión nuevamente.';
      } else if (status === 403) {
        errorMessage = 'No tiene permisos para acceder a este tablero.';
      } else if (status === 404) {
        errorMessage = 'El tablero no existe o ha sido eliminado.';
      } else {
        errorMessage = `Error del servidor (${status}): ${error.response.statusText || 'Error desconocido'}`;
      }
    } else if (error.code === 'ECONNABORTED') {
      errorMessage = 'La solicitud tomó demasiado tiempo. Compruebe su conexión.';
    } else if (error.message) {
      errorMessage = error.message;
    }
    
    // Despachar error
    dispatch({
      type: GET_COLUMNS_ERROR,
      payload: errorMessage
    });

    // Devolver objeto con error
    return {
      payload: [],
      error: errorMessage,
      hasMore: false
    };
  }
};

/**
 * Crea una nueva columna
 */
export const createColumn = (boardId, columnData) => async (dispatch) => {
  try {
    dispatch({ type: CREATE_COLUMN });

    const { token, name, position, id_customer, meta_data } = columnData;

    // Validar datos requeridos
    if (!name) {
      throw new Error('El nombre de la columna es obligatorio');
    }

    if (position === undefined || position === null) {
      throw new Error('La posición de la columna es obligatoria');
    }

    if (!id_customer) {
      throw new Error('El ID del cliente es obligatorio');
    }

    if (!token) {
      throw new Error('El token de autenticación es obligatorio');
    }

    // Asegurarnos de que el token tenga el formato correcto
    const authToken = token.startsWith('Bearer ') ? token : `Bearer ${token}`;

    // Preparar parámetros de la solicitud
    const params = {
      name: name.trim(),
      position: Number(position),
      id_customer
    };

    // Procesar metadatos si existen
    if (meta_data) {
      params.meta_data = typeof meta_data === 'object'
        ? JSON.stringify(meta_data)
        : meta_data;
    }

    // Realizar la petición POST
    const response = await axios.post(
      `/kanban/boards/${boardId}/columns`,
      null,
      {
        params,
        headers: {
          Authorization: authToken
        }
      }
    );

    // Formatear la columna para el estado de Redux
    const responseData = response.data;
    const formattedColumn = {
      id: responseData.id,
      name: responseData.name,
      position: responseData.position,
      board_id: boardId,
      id_customer: responseData.id_customer || id_customer,
      tickets: [],  // Inicializar con array vacío
      ...responseData  // Mantener otros campos que puedan venir del backend
    };

    // Actualizar el estado de Redux
    dispatch({
      type: CREATE_COLUMN_SUCCESS,
      payload: formattedColumn
    });

    return formattedColumn;
  } catch (error) {
    // Manejar errores específicos
    let errorMessage = 'Error al crear la columna';

    if (error.response) {
      // Error de la API con respuesta
      const status = error.response.status;
      const data = error.response.data;

      if (status === 422) {
        // Error de validación
        errorMessage = data.detail && typeof data.detail === 'string'
          ? `Error de validación: ${data.detail}`
          : 'Error de validación en los datos';
      } else if (status === 401) {
        errorMessage = 'No autorizado: verifique sus credenciales';
      } else if (status === 404) {
        errorMessage = 'Tablero no encontrado';
      } else if (data && data.message) {
        errorMessage = data.message;
      }
    } else if (error.message) {
      // Error con mensaje personalizado
      errorMessage = error.message;
    }

    // Actualizar el estado de Redux con el error
    dispatch({
      type: CREATE_COLUMN_ERROR,
      payload: errorMessage
    });

    // Propagar el error para manejo adicional
    throw new Error(errorMessage);
  }
};

/**
 * Actualiza una columna existente
 */
export const updateColumn = (columnId, updateData) => async (dispatch) => {
  dispatch({ type: UPDATE_COLUMN });

  try {
    // Extraer el token si está presente en los datos de actualización
    const { token, ...dataToUpdate } = updateData;

    // Preparar los parámetros para la solicitud
    const params = {};

    // Añadir solo los campos definidos
    if (dataToUpdate.name !== undefined) params.name = dataToUpdate.name;
    if (dataToUpdate.position !== undefined) params.position = dataToUpdate.position;

    // Manejar metadatos - asegurarse de que sea un string JSON si existe
    if (dataToUpdate.meta_data !== undefined) {
      // Principio SRP: Este bloque solo se encarga de procesar los metadatos.
      if (typeof dataToUpdate.meta_data === 'object') {
        params.meta_data = JSON.stringify(dataToUpdate.meta_data);
      } else {
        try {
          JSON.parse(dataToUpdate.meta_data);
          params.meta_data = dataToUpdate.meta_data;
        } catch (e) {
          params.meta_data = '{}';
        }
      }
    }

    // Configurar headers con el token de autenticación
    const headers = token ? { Authorization: `Bearer ${token}` } : {};

    // Realizar la solicitud PUT con los parámetros como query params
    const response = await axios.put(
      `/kanban/columns/${columnId}`,
      null,
      {
        params,
        headers
      }
    );

    dispatch({
      type: UPDATE_COLUMN_SUCCESS,
      payload: response.data
    });

    return response.data;
  } catch (error) {
    console.error('Error al actualizar columna:', error);
    dispatch({
      type: UPDATE_COLUMN_ERROR,
      payload: error.message
    });
    throw error;
  }
};

/**
 * Elimina una columna
 */
export const deleteColumn = (columnId, token) => async (dispatch, getState) => {
  dispatch({ type: DELETE_COLUMN });

  try {
    // Si no se proporciona token, intentar obtenerlo del estado
    let authToken = token;

    if (!authToken) {
      // Intentar obtener el token del hook useToken (si está disponible en el componente que llama)
      const state = getState();
      if (state.auth && state.auth.token) {
        authToken = state.auth.token;
      }
    }

    // Configurar headers con el token de autenticación
    const headers = authToken ? { Authorization: `Bearer ${authToken}` } : {};

    // Realizar la solicitud DELETE con los headers
    const response = await axios.delete(`/kanban/columns/${columnId}`, { headers });

    dispatch({
      type: DELETE_COLUMN_SUCCESS,
      payload: columnId
    });

    // Retornar la respuesta para que el componente sepa si fue eliminada o ocultada
    return response.data;
  } catch (error) {
    console.error('Error al eliminar columna:', error);
    dispatch({
      type: DELETE_COLUMN_ERROR,
      payload: error.message
    });
    throw error;
  }
};

/**
 * Actualiza las posiciones de múltiples columnas en una sola petición batch optimizada
 */
export const updateColumnPositionsBatch = (updates, token) => async (dispatch, getState) => {
  try {
    dispatch({ type: REORDER_COLUMNS });

    // Validar parámetros
    if (!Array.isArray(updates) || updates.length === 0) {
      throw new Error('No hay actualizaciones para procesar');
    }

    if (!token) {
      throw new Error('Token de autenticación no proporcionado');
    }

    // Validar formato de cada actualización
    for (const update of updates) {
      if (!update.id || typeof update.position !== 'number') {
        throw new Error('Cada actualización debe tener id y una "position" numérica');
      }
    }

    // Obtener el estado actual para preservar los tickets
    const state = getState();
    const currentColumns = state.kanban.columns?.items || [];

    // Asegurarnos de que el token tenga el formato correcto
    const authToken = token.startsWith('Bearer ') ? token : `Bearer ${token}`;

    // Realizar la petición batch al nuevo endpoint
    const response = await axios.put('/kanban/columns/batch/positions', {
      updates: updates
    }, {
      headers: {
        'Authorization': authToken,
        'Content-Type': 'application/json'
      },
      timeout: 15000 // Mayor timeout para operaciones batch
    });

    // Preservar los tickets de las columnas actualizadas
    const updatedColumnsWithTickets = response.data.updated_columns.map(updatedCol => {
      const currentCol = currentColumns.find(col => String(col.id) === String(updatedCol.id));
      
      return {
        ...updatedCol,
        tickets: currentCol && currentCol.tickets ? currentCol.tickets : []
      };
    });

    dispatch({
      type: REORDER_COLUMNS_SUCCESS,
      payload: updatedColumnsWithTickets
    });

    return {
      success: true,
      updatedColumns: updatedColumnsWithTickets,
      message: response.data.message
    };

  } catch (error) {
    let errorMessage = 'Error al actualizar posiciones de columnas';
    
    if (error.response) {
      const status = error.response.status;
      if (status === 400) {
        errorMessage = `Error de validación: ${error.response.data?.detail || 'Datos inválidos'}`;
      } else if (status === 404) {
        errorMessage = 'Una o más columnas no fueron encontradas';
      } else if (status === 500) {
        errorMessage = 'Error interno del servidor';
      } else {
        errorMessage = `Error del servidor (${status}): ${error.response.statusText || 'Error desconocido'}`;
      }
    } else if (error.code === 'ECONNABORTED') {
      errorMessage = 'La operación tomó demasiado tiempo. Verifique su conexión.';
    } else if (error.message) {
      errorMessage = error.message;
    }

    dispatch({
      type: REORDER_COLUMNS_ERROR,
      payload: errorMessage
    });

    return {
      success: false,
      error: errorMessage
    };
  }
};



/**
 * Reordena columnas de manera simple (similar al movimiento de tickets)
 * Sin usar BatchOperationModal - solo actualización directa
 */
export const reorderColumnsSimple = (columns, sourceIndex, destinationIndex, token) => async (dispatch) => {
  try {
    dispatch({ type: REORDER_COLUMNS });

    // Validaciones básicas
    if (!Array.isArray(columns) || columns.length === 0) {
      throw new Error('No hay columnas para reordenar');
    }

    if (sourceIndex === destinationIndex) {
      // No hay cambio, retornar las columnas actuales
      return columns;
    }

    if (!token) {
      throw new Error('Token de autenticación requerido');
    }

    // Preservar tickets originales
    const originalColumns = [...columns].map(col => ({
      ...col,
      tickets: [...(col.tickets || [])]
    }));

    // Crear nuevo orden localmente
    const reorderedColumns = [...originalColumns];
    const [movedColumn] = reorderedColumns.splice(sourceIndex, 1);
    reorderedColumns.splice(destinationIndex, 0, movedColumn);

    // Actualizar posiciones
    const updates = reorderedColumns.map((col, index) => ({
      id: col.id,
      position: index + 1
    }));

    // Asegurar formato del token
    const authToken = token.startsWith('Bearer ') ? token : `Bearer ${token}`;

    // Llamar al endpoint batch pero sin modal
    const response = await axios.put('/kanban/columns/batch/positions', {
      updates: updates
    }, {
      headers: {
        'Authorization': authToken,
        'Content-Type': 'application/json'
      },
      timeout: 10000
    });

    // Preservar tickets en las columnas actualizadas
    const updatedColumnsWithTickets = response.data.updated_columns.map(updatedCol => {
      const originalCol = originalColumns.find(col => String(col.id) === String(updatedCol.id));
      
      return {
        ...updatedCol,
        tickets: originalCol && originalCol.tickets ? originalCol.tickets : []
      };
    });

    dispatch({
      type: REORDER_COLUMNS_SUCCESS,
      payload: updatedColumnsWithTickets
    });

    return updatedColumnsWithTickets;

  } catch (error) {
    console.error('[SIMPLE REORDER] ❌ Error:', error);

    let errorMessage = 'Error al reordenar columnas';
    
    if (error.response) {
      const status = error.response.status;
      if (status === 400) {
        errorMessage = 'Datos de reordenamiento inválidos';
      } else if (status === 404) {
        errorMessage = 'Columnas no encontradas';
      } else if (status === 500) {
        errorMessage = 'Error del servidor';
      }
    } else if (error.code === 'ECONNABORTED') {
      errorMessage = 'Tiempo de espera agotado';
    } else if (error.message) {
      errorMessage = error.message;
    }

    dispatch({
      type: REORDER_COLUMNS_ERROR,
      payload: errorMessage
    });

    throw new Error(errorMessage);
  }
};